import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import { ThemeContext } from "../../src/ThemeContext";
import { IoMdArrowBack } from "react-icons/io";
import "../tables/expense.css";
import "../Responsive/TableResponsive.css";
import "../Responsive/FormResponsive.css";

function ExpenseView({ isSidebarExpanded }) {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { theme }    = useContext(ThemeContext);

  const [report, setReport]   = useState(null);
  const [items, setItems]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen]       = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity]               = useState("success");

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSeverity(sev);
    setSnackbarOpen(true);
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchReport = async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const res = await axios.get(`${API_URL}/api/expense_report/${id}`, {
        headers: { Authorization: authKey },
      });
      if (res.status === 200) {
        setReport(res.data.report);
        setItems(res.data.items || []);
        setSummary(res.data.summary);
      }
    } catch {
      showSnackbar("Error fetching expense report", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) fetchReport(); }, [id]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  };

  const formatCurrency = (val) =>
    `₹${parseFloat(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatReportMonth = (rm) => {
    if (!rm) return "N/A";
    try {
      return new Date(rm + "-01").toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    } catch { return rm; }
  };

  const formatCreatedAt = (ts) => {
    if (!ts) return "N/A";
    try {
      return new Date(ts).toLocaleString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
      }) + " IST";
    } catch { return ts; }
  };

  const getCategoryStyle = (category) => {
    const map = {
      "IOCL Depot":    { bg: "#e3f0fb", color: "#1a5276" },
      "Keshav Encon":  { bg: "#eafaf1", color: "#1e8449" },
      "Travel":        { bg: "#fef9e7", color: "#b7950b" },
      "Fuel":          { bg: "#f5e6fa", color: "#7d3c98" },
      "Accommodation": { bg: "#fdebd0", color: "#a04000" },
      "Food":          { bg: "#e8f8f5", color: "#148f77" },
      "General":       { bg: "#f0f0f0", color: "#555" },
    };
    return map[category] || { bg: "#f0f0f0", color: "#555" };
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="no-users-container">
          <div className="no-users-message">Expense Report Not Found</div>
        </div>
      </div>
    );
  }

  // Build info cards exactly as in the image:
  // Month | Year | Status (row 1) — Description | Created At (row 2)
  // We adapt to expense report fields.
  const claimPeriod =
    report.claim_period_start && report.claim_period_end
      ? `${formatDate(report.claim_period_start)} — ${formatDate(report.claim_period_end)}`
      : "N/A";

  // Row 1: Report Month | Employee | Location
  // Row 2: Advance | Balance B/F | Claim Period
  // Row 3: Created At (spanning)
  const infoCards = [
    { label: "Report Month", value: formatReportMonth(report.report_month) },
    { label: "Employee",     value: report.employee_name || "N/A" },
    { label: "Location",     value: report.location || "N/A" },
    { label: "Advance",      value: formatCurrency(report.advance_amount) },
    { label: "Balance B/F",  value: formatCurrency(report.balance_brought_forward) },
    { label: "Claim Period", value: claimPeriod },
    { label: "Created At",   value: formatCreatedAt(report.created_at) },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">

          {/* ── Header — back arrow + title, NO edit/export ── */}
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <div className="expense_view_header_title">
                  <IoMdArrowBack
                    className="expense_back_icon"
                    onClick={() => navigate("/all-expenses")}
                  />
                  Expense Report Details
                </div>
              </div>
              {/* No right-side actions on view page */}
              <div className="data-form-heading-content-right-side" />
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-form-body">

            {/* ── Info cards grid (matches image layout) ── */}
            <div className="expense_info_cards_grid">
              {infoCards.map((card) => (
                <div className="expense_info_card" key={card.label}>
                  <span className="expense_info_card_label">{card.label}:&nbsp;</span>
                  <span className="expense_info_card_value">{card.value}</span>
                </div>
              ))}
            </div>

            {/* ── Summary cards ── */}
            {summary && (
              <div className="expense_view_summary_row">
                {[
                  { label: "Total Items",   value: summary.total_items },
                  { label: "Total Expense", value: formatCurrency(summary.total_expense), highlight: true },
                  { label: "Advance",       value: formatCurrency(summary.advance_amount) },
                  { label: "Balance C/F",   value: formatCurrency(summary.balance_carried_forward) },
                ].map((card) => (
                  <div
                    key={card.label}
                    className={`expense_view_summary_card${card.highlight ? " highlight" : ""}`}
                  >
                    <div className="expense_view_summary_card_label">{card.label}</div>
                    <div className="expense_view_summary_card_value">{card.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Items table ── */}
            <div className="expense_section_label">Expense Items</div>
            <div className="data-list-tables">
              <table className="table multilevel-data">
                <thead>
                  <tr>
                    <th className="center-alignment">SR. NO.</th>
                    <th>DATE</th>
                    <th>BILL / RECEIPT NO</th>
                    <th>EXPENSE TYPE</th>
                    <th className="center-alignment">AMOUNT (₹)</th>
                    <th>PURPOSE / DESCRIPTION</th>
                    <th>VENDOR / REMARK</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                        No items found
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => {
                      const catStyle = getCategoryStyle(item.expense_category);
                      return (
                        <tr key={item.id || idx} className="hoverable-row">
                          <td className="center-alignment">{idx + 1}</td>
                          <td>{formatDate(item.expense_date)}</td>
                          <td>{item.bill_receipt_no || "NA"}</td>
                          <td>
                            <span
                              className="expense_category_badge"
                              style={{ background: catStyle.bg, color: catStyle.color }}
                            >
                              {item.expense_category || "General"}
                            </span>
                          </td>
                          <td
                            className="center-alignment"
                            style={{ fontWeight: 600, color: "#2980B9" }}
                          >
                            {formatCurrency(item.amount)}
                          </td>
                          <td>{item.purpose_description || "NA"}</td>
                          <td>{item.vendor_remark || "NA"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-cancel-button"
                onClick={() => navigate("/all-expenses")}
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ExpenseView;